import * as React from 'react';
import { read, utils, type WorkBook } from 'xlsx';
import map from 'lodash/map';
import { cn } from '@ai-content-hub/content-kit';

export interface XlsxGridViewerProps {
    data: ArrayBuffer;
    className?: string;
}

export const XlsxGridViewer: React.FC<XlsxGridViewerProps> = ({ data, className }) => {
    const [workbook, setWorkbook] = React.useState<WorkBook | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [sheetIndex, setSheetIndex] = React.useState(0);

    React.useEffect(() => {
        try {
            const wb = read(data, { type: 'array' });
            setWorkbook(wb);
            setSheetIndex(0);
            setError(null);
        } catch {
            setWorkbook(null);
            setError('Could not open this spreadsheet.');
        }
    }, [data]);

    if (error) {
        return (
            <div className={cn('office-xlsx-viewer office-xlsx-viewer--error p-4 text-sm text-muted-foreground', className)}>
                {error}
            </div>
        );
    }

    if (!workbook) {
        return (
            <div className={cn('office-xlsx-viewer office-xlsx-viewer--loading p-4 text-sm text-muted-foreground', className)}>
                Loading spreadsheet…
            </div>
        );
    }

    const names = workbook.SheetNames;
    const activeName = names[sheetIndex];
    const worksheet = activeName ? workbook.Sheets[activeName] : undefined;
    const rows: unknown[][] = worksheet
        ? (utils.sheet_to_json(worksheet, { header: 1, defval: '' }) as unknown[][])
        : [];

    return (
        <div className={cn('office-xlsx-viewer flex max-h-full min-h-0 min-w-0 flex-col gap-2', className)}>
            {names.length > 1 ? (
                <div className="office-xlsx-viewer-tabs flex flex-wrap gap-1 border-b border-border pb-2">
                    {map(names, (name, i) => (
                        <button
                            key={name}
                            type="button"
                            onClick={() => setSheetIndex(i)}
                            className={cn(
                                'office-xlsx-viewer-tab cursor-pointer rounded-sm px-3 py-1 text-xs font-medium transition-colors duration-200',
                                i === sheetIndex
                                    ? 'bg-background text-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground',
                            )}
                        >
                            {name}
                        </button>
                    ))}
                </div>
            ) : null}
            <div className="office-xlsx-viewer-scroll min-h-0 flex-1 overflow-auto rounded-sm border border-border bg-muted/20">
                <table className="office-xlsx-viewer-grid w-full border-collapse text-left text-xs">
                    <tbody>
                        {map(rows, (row, ri) => (
                            <tr key={ri} className="office-xlsx-viewer-row border-b border-border/60">
                                {map(row as unknown[], (cell, ci) => (
                                    <td
                                        key={ci}
                                        className="office-xlsx-viewer-cell max-w-[240px] truncate border-r border-border/60 px-2 py-1 align-top text-foreground last:border-r-0"
                                        title={cell === null || cell === undefined ? '' : String(cell)}
                                    >
                                        {cell === null || cell === undefined ? '' : String(cell)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
